/* eslint-disable no-secrets/no-secrets */
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), method, data, additionalHeaders);
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class LoadSaveService
 */
export default class LoadSaveService {
    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        return processSubmission('saveAndQuote', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {Object} data the retrieval payload (QuoteRetrievalDTO)
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static retrieveSubmission(data, additionalHeaders = {}) {
        return processSubmission('retrieve', [data], additionalHeaders);
    }

    /**
     * Updates a quoted submission with LOB Data.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmissionWithLOBData(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmissionWithLOBData', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {string} quoteID quoteID of submission that needs to be found
     * @param {string} sessionUUID current session id
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrievePaymentPlans(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission('retrievePaymentPlans', [quoteID, sessionUUID], additionalHeaders);
    }

    /**
     * Binds the submission.
     *
     * @param {Object} data the submission to be bound
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static bindSubmission(data, additionalHeaders = {}) {
        return processSubmission('bind', [data], additionalHeaders);
    }

    /**
     * Updates an existing draft submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateDraftSubmission', [data], additionalHeaders);
    }

    /**
     * Updates a quoted submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmission', [data], additionalHeaders);
    }

    /**
     * Updates coverages.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} sessionUUID sessionUUID
     * @param {Object} lobCoverages the coverages to be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static updateCoverages(quoteID, sessionUUID, lobCoverages, additionalHeaders = {}) {
        return processSubmission(
            'updateCoverages',
            [quoteID, sessionUUID, lobCoverages],
            additionalHeaders
        );
    }

    /**
     * Approve Underwriting Issue.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} uwIssue uwIssue
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static approveUnderwritingIssue(quoteID, uwIssue, additionalHeaders = {}) {
        return processSubmission(
            'approveUnderwritingIssue',
            [quoteID, uwIssue],
            additionalHeaders
        );
    }

    static searchExistingContactRecords_Ext(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'searchExistingContactRecords_Ext',
            submission,
            additionalHeaders
        );
    }

    static createNewAccountContact_Ext(data, accountNumber, additionalHeaders = {}) {
        return processSubmission('createNewAccountContact_Ext',[data, accountNumber], additionalHeaders);
    }

    static createOrUpdatePolicyLocation_Ext(submission, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'createOrUpdatePolicyLocation_Ext',
            submission,
            additionalHeaders
        );
    }

    static updatePrimaryNamedInsured_Ext(quoteID, contactPublicId, additionalHeaders = {}) {
        return processSubmission('updatePrimaryNamedInsured_Ext',[quoteID, contactPublicId], additionalHeaders);
    }

    static getAccountContacts_Ext(data, additionalHeaders = {}) {
        return processSubmission( 'getAccountContacts_Ext', [data,false], additionalHeaders );
    }

    static addOrUpdatePolicyAddress_Ext(quoteID, address, additionalHeaders = {}) {
        return processSubmission( 'addOrUpdatePolicyAddress_Ext', [quoteID, address], additionalHeaders );
    }

    static updateOfferings_Ext(quoteID, selectedOffering, additionalHeaders = {}) {
        return processSubmission('updateOfferings_Ext',[quoteID, selectedOffering], additionalHeaders);
    }

    static updateDeclaration_Ext(quoteID, bindingDataDTO, additionalHeaders = {}) {
        return processSubmission('updateDeclaration_Ext',[quoteID, bindingDataDTO], additionalHeaders);
    }

    static createVehicleRisk_Ext(quoteID, sessionUUID, vehicleData, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/prvcoverables'),
            'addNewVehLocRisk',
            [quoteID, sessionUUID, vehicleData],
            additionalHeaders
        );
    }

    static createLocRisk_Ext(quoteID, sessionUUID, locRiskData, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/pplcoverables'),
            'addNewLocRisk',
            [quoteID, sessionUUID, locRiskData],
            additionalHeaders
        );
    }

    static printQuoteDocument(quoteID, docType, additionalHeaders = {}) {
        return processSubmission('printQuoteDocument',[quoteID, docType], additionalHeaders);
    }

    static printQuoteDocuments_Ext(quoteID, additionalHeaders = {}) {
        return processSubmission('printQuoteDocuments_Ext',[quoteID], additionalHeaders);
    }

    static addGenDecDocumentEvent_Ext(policyNumber, additionalHeaders = {}) {
        return processSubmission('addGenDecDocumentEvent_Ext',[policyNumber], additionalHeaders);
    }
	
	static retrieveClassType_Ext(offeringType, service, additionalHeaders = {}) {
        return processSubmission('retrieveClassType_Ext',[offeringType, service], additionalHeaders);
    }

    static getPCTEquBreClassCodes_Ext(pctEquBreClassCodeDTO, additionalHeaders = {}) {
        return processSubmission('getPCTEquBreClassCodes_Ext',[pctEquBreClassCodeDTO], additionalHeaders);
    }

    static getRMManufacturerClassCodes_Ext(manufacturerSearchReqDTO, additionalHeaders = {}) {
        return processSubmission('getRMManufacturerClassCodes_Ext',[manufacturerSearchReqDTO], additionalHeaders);
    } 

    static getRecMarViolationClassCodes_Ext(violationSearchReqDTO, additionalHeaders = {}) {
        return processSubmission('getRecMarViolationClassCodes_Ext',[violationSearchReqDTO], additionalHeaders);
    }

    static getSpaWellOpeEstClassCodes_Ext(opeEstClassCodeDTO, additionalHeaders = {}) {
        return processSubmission('getSpaWellOpeEstClassCodes_Ext',[opeEstClassCodeDTO], additionalHeaders);
    }

    static getRVOccupationClassCodes_Ext(occupationSearchReqDTO, additionalHeaders = {}) {
        return processSubmission('getRVOccupationClassCodes_Ext',[occupationSearchReqDTO], additionalHeaders);
    } 
    
    static getProInStoStorageFacClassCodes_Ext(storageFacilityClassCodeDTO, additionalHeaders = {}) {
        return processSubmission('getProInStoStorageFacClassCodes_Ext',[storageFacilityClassCodeDTO], additionalHeaders);
    }

    static getRiskClassification(speOpe, additionalHeaders = {}) {
        return processSubmission('getRiskClassification',[speOpe], additionalHeaders);
    }

    static getDwellingOccClassCodes_Ext(occFieClassCodeDTO, additionalHeaders = {}) {
        return processSubmission('getDwellingOccClassCodes_Ext',[occFieClassCodeDTO], additionalHeaders);
    }

}